/*----------------------------------
- MARKET MEGA MENU
----------------------------------*/

import { LANDING_MARKET_MENU_ITEMS } from '@/client/catalogs/landing/marketMenu';
import { LANDING_MENU_PANEL_COPY } from '@/client/catalogs/landing/menuPanels';
import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import MegaMenuPanel from './MegaMenuPanel';
import MenuPanelHeader from './MenuPanelHeader';
import MenuRow from './MenuRow';
import { formatMenuEditionDate } from './menuNewsDate';
import type { MenuNavigateHandler } from './navClick';

/*----------------------------------
- TYPES
----------------------------------*/

type MarketMegaMenuProps = {
    id: string;
    isOpen: boolean;
    /** The news preview the header already receives. Only its latest edition date is read. */
    news?: LandingHeaderDomainsNavigation['news'];
    onNavigate: MenuNavigateHandler;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const COPY = LANDING_MENU_PANEL_COPY.market;

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * What the market is doing: Sales, News, TLDs, Trending Domains, Business Ideas and Agenda have a
 * page. Auctions has none, so it renders as a disabled row with a Soon pill rather than as a link,
 * and they carry no fact: a figure about a surface nobody can open is a claim the product
 * cannot back.
 */
export default function MarketMegaMenu({ id, isOpen, news, onNavigate }: MarketMegaMenuProps) {
    const updatedOn = formatMenuEditionDate(news?.latest?.dateKey);

    return (
        <MegaMenuPanel id={id} testId="market-mega-menu" isOpen={isOpen} width="medium">
            <MenuPanelHeader
                title={COPY.title}
                lead={COPY.lead}
                aside={
                    updatedOn ? (
                        <span class="inline-flex items-center gap-2 text-[12.5px] font-semibold text-[#66696b]">
                            News updated {updatedOn}
                        </span>
                    ) : null
                }
            />

            <ul class="mt-[22px] grid grid-cols-2 gap-x-8" data-testid="market-menu-rows">
                {LANDING_MARKET_MENU_ITEMS.map((item) => (
                    <li key={item.id}>
                        <MenuRow
                            href={item.href}
                            status={item.status}
                            label={item.label}
                            description={item.description}
                            iconName={item.iconName}
                            trailing={item.fact}
                            onClick={() => item.href && onNavigate(item.id, item.href)}
                        />
                    </li>
                ))}
            </ul>
        </MegaMenuPanel>
    );
}
